import {
  Component,
  Element,
  h,
  Host,
  Method,
  Prop,
  State,
  Watch
} from "@stencil/core";

import {
  ChTreeViewRenderCustomEvent,
  TreeViewItemModel,
  TreeViewItemOpenReferenceInfo,
  TreeViewModel
} from "@genexus/chameleon-controls-library";
import { getIconPath, MercuryBundles } from "@genexus/mercury";
import { config } from "../../common/config";
import { Locale } from "../../common/locale";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/edit",
  "components/tree-view",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];
const SEARCH_ICON = getIconPath({
  category: "system",
  name: "search",
  colorType: "on-surface"
});

const INFORMATION_ICON = getIconPath({
  category: "system",
  name: "information",
  colorType: "on-interactive"
});

@Component({
  tag: "gx-ide-search",
  styleUrl: "search.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/search"]
})
export class GxIdeSearch {
  #componentLocale: any;
  #searchInputValue: string = "";

  @Element() el: HTMLGxIdeSearchElement;

  /**
   * Holds the search results as a TreeViewModel
   */
  @State() searchResultModel: TreeViewModel = [];

  /**
   * Tracks if a search has been performed
   */
  @State() hasSearched = false;

  /**
   * Tracks if a search is currently in progress
   */
  @State() isSearching = false;

  /**
   * Indicates whether a Knowledge Base is currently open
   */
  @Prop() readonly isKbOpen: boolean = false;
  @Watch("isKbOpen")
  valueChangeHandler(newValue: boolean) {
    if (!newValue) {
      this.#searchInputValue = "";
      this.searchResultModel = [];
      this.hasSearched = false;
      this.isSearching = false;
    }
  }

  /**
   * Callback to fetch search results
   */
  @Prop() readonly searchCallback!: (
    searchParams: SearchParamsData
  ) => Promise<SearchResultData[]>;

  /**
   * Callback invoked when a search result item is double-clicked
   */
  @Prop() readonly itemDoubleClickCallback?: ItemDoubleClickCallback;

  /**
   * Method to show or hide the settings panel, can be called from the outside to control the visibility of the settings
   */
  @Method()
  async showSettings(_show: boolean): Promise<void> {
    return Promise.resolve();
  }

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
  }

  #searchInputHandler = async (event: CustomEvent<string> | InputEvent) => {
    if (!this.isKbOpen) {
      return;
    }

    const inputElement = event.target as HTMLChEditElement;
    const searchText = inputElement?.value?.trim();
    this.#searchInputValue = searchText;

    if (!searchText) {
      this.searchResultModel = [];
      this.hasSearched = false;
      this.isSearching = false;
      return;
    }

    this.hasSearched = true;
    this.isSearching = true;
    this.searchResultModel = [];

    try {
      const searchResultData = await this.searchCallback({
        text: searchText,
        onlyTitles: false,
        includeSubModules: true,
        allBranches: false,
        forLLM: false
      });

      this.searchResultModel =
        this.#mapSearchResultToTreeViewModel(searchResultData);
    } finally {
      this.isSearching = false;
    }
  };

  #searchInputKeyDownHandler = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      this.#searchInputHandler(event as any);
    }
  };

  #mapSearchResultToTreeViewModel = (
    searchResultData: SearchResultData[]
  ): TreeViewModel => {
    if (!searchResultData || searchResultData.length === 0) {
      return [];
    }
    return [
      {
        id: "root",
        caption: `${this.#componentLocale.labels.results} (${searchResultData.length})`,
        expanded: true,
        parts: "root-item",
        items: searchResultData.map(
          (item): TreeViewItemModel => ({
            id: item.objectId,
            startImgSrc: item.objectIcon,
            caption: item.objectName,
            leaf: true,
            parts: "search-item"
          })
        )
      }
    ];
  };

  #treeItemDoubleClickHandler = async (
    event: ChTreeViewRenderCustomEvent<TreeViewItemOpenReferenceInfo>
  ) => {
    const itemId = event.detail.id;
    await this.itemDoubleClickCallback?.(itemId);
  };

  #renderSearchResults = () => {
    if (!this.isKbOpen) {
      return (
        <gx-ide-empty-state
          stateTitle={this.#componentLocale.noKbOpenState.title}
          stateDescription={this.#componentLocale.noKbOpenState.description}
          stateIconSrc={INFORMATION_ICON}
          isAnimated={true}
        ></gx-ide-empty-state>
      );
    }

    if (this.isSearching) {
      return (
        <gx-ide-loader
          loaderTitle={this.#componentLocale.loader.title}
          show={true}
        ></gx-ide-loader>
      );
    }

    if (this.searchResultModel.length > 0) {
      return (
        <ch-tree-view-render
          class="tree-view"
          model={this.searchResultModel}
          expandableButton="no"
          expandOnClick={false}
          onItemOpenReference={this.#treeItemDoubleClickHandler}
        />
      );
    }

    if (this.hasSearched) {
      return (
        <gx-ide-empty-state
          stateTitle={this.#componentLocale.emptyState.title}
          stateDescription={this.#componentLocale.emptyState.description}
          stateIconSrc={INFORMATION_ICON}
          isAnimated={true}
        ></gx-ide-empty-state>
      );
    }

    return null;
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class={"spacing-body search"}>
          <header class="search__header">
            <ch-edit
              id="search-input"
              class="input"
              accessibleName={this.#componentLocale.accessibleName}
              placeholder={this.#componentLocale.placeholders.search}
              debounce={config.inputDebounce}
              onInput={this.#searchInputHandler}
              onKeyDown={this.#searchInputKeyDownHandler}
              type="search"
              startImgSrc={SEARCH_ICON}
              disabled={!this.isKbOpen}
              value={this.#searchInputValue}
            ></ch-edit>
          </header>

          <main class="search__results">{this.#renderSearchResults()}</main>
        </section>
      </Host>
    );
  }
}

export type SearchResultData = {
  objectId: string;
  objectName: string;
  objectTypeName: string;
  objectTypeId: string;
  objectModuleName: string;
  objectUri: string;
  objectIcon: string;
  content: string;
  preview: string;
};

export type SearchParamsData = {
  moduleGuid?: string;
  objectTypeName?: string;
  text: string;
  tags?: string;
  count?: number;
  onlyTitles: boolean;
  includeSubModules: boolean;
  allBranches: boolean;
  forLLM: boolean;
};

export type ItemDoubleClickCallback = (itemId: string) => Promise<void>;

export type SearchCallback = (
  searchParams: SearchParamsData
) => Promise<SearchResultData[]>;
